import React, { useState, useEffect } from "react";
import Img1 from "../assets/testimonials/Img1.jpg";
import Img2 from "../assets/testimonials/Img2.jpg";
import Img3 from "../assets/testimonials/Img3.jpg";
import Img4 from "../assets/testimonials/Img4.jpg";
import Img5 from "../assets/testimonials/Img5.jpg";
import Img6 from "../assets/testimonials/Img6.jpg";
import Img7 from "../assets/testimonials/Img7.jpg";
import Img8 from "../assets/testimonials/Img8.jpg";
/* ===================== DATA ===================== */

const testimonials = [
  {
    img: Img1,
    quote:
      "Working with TWA changed my life. It connected me with great opportunities and kept me away from negative influences and on the right track. It also gave me a job that kept me busy.",
    name: "Gerarrd",
    role: "Job Seeker",
    subtitle: "Construction",
  },
  {
    img: Img2,
    quote:
      "The support I received from TWA made all the difference. They saw my potential, not just my past.",
    name: "Program Graduate",
    role: "Graduate",
    subtitle: "Manufacturing",
  },
  {
    img: Img3,
    quote:
      "Working with TWA opened doors I never thought would be available to me. I'm grateful for their belief in second chances.",
    name: "TWA Alumni",
    role: "Alumni",
    subtitle: "Retail",
  },
  {
    img: Img4,
    quote:
      "I am thankful for the opportunity I received and everything TWA did to help me. It gave me hope not to give up on my dreams.",
    name: "Program Participant",
    role: "Job Seeker",
    subtitle: "TWA Jobseeker",
  },
  {
    img: Img5,
    quote:
      "TWA helped me stay focused and gave me a sense of direction during a critical time in my life.",
    name: "Program Participant",
    role: "Job Seeker",
    subtitle: "Workforce Program",
  },
  {
    img: Img6,
    quote:
      "They looked beyond my past and helped me build a future I can be proud of. ",
    name: "Program Graduate",
    role: "Graduate",
    subtitle: "Manufacturing",
  },
  {
    img: Img7,
    quote:
      "The opportunities provided through TWA helped me regain confidence and stability.",
    name: "TWA Alumni",
    role: "Alumni",
    subtitle: "Customer Service",
  },
  {
    img: Img8,
    quote:
      "TWA gave me the motivation and support I needed to believe in myself again.",
    name: "Program Participant",
    role: "Job Seeker",
    subtitle: "Career Readiness",
  },
];

/* ===================== CONSTANTS ===================== */

const CARD_W = 350;
const CARD_H = 450;
const GAP = 16;

/* ===================== ARROW ===================== */

const Arrow = ({ direction, onClick }) => {
  const isLeft = direction === "left";

  return (
    <button
      onClick={onClick}
      aria-label={direction}
      className="group flex items-center justify-center h-[45px] w-[60px]"
    >
      <svg width="60" height="45" viewBox="0 0 60 45">
        <g
          className="transition-transform duration-500 group-hover:scale-x-125"
          style={{ transformOrigin: isLeft ? "20px 22.5px" : "40px 22.5px" }}
        >
          <line
            x1={isLeft ? "65" : "-5"}
            y1="22.5"
            x2={isLeft ? "18" : "42"}
            y2="22.5"
            stroke="#003DA5"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <polyline
            points={isLeft ? "24,14 16,22.5 24,31" : "36,14 44,22.5 36,31"}
            fill="none"
            stroke="#003DA5"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </svg>
    </button>
  );
};

/* ===================== MAIN COMPONENT ===================== */

const TestimonialsSection = () => {
  const [currentPos, setCurrentPos] = useState(0);
  const [activeCard, setActiveCard] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isInstant, setIsInstant] = useState(false);

  const total = testimonials.length;

  /* --- Detect screen size --- */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const visibleCards = isMobile ? 1 : 4;

  // Extended list length = real items + clones on both ends
  const extendedLength = total + 2 * visibleCards;

  // First and last "real" positions within extended list
  const firstRealIndex = visibleCards;
  const lastRealIndex = visibleCards + total - 1;

  // Initialize position whenever layout changes
  useEffect(() => {
    setIsInstant(true);
    setCurrentPos(firstRealIndex);
  }, [visibleCards, firstRealIndex]);

  // After instant jump, re-enable smooth transition
  useEffect(() => {
    if (isInstant) {
      // next animation frame -> smooth transitions again
      const id = requestAnimationFrame(() => setIsInstant(false));
      return () => cancelAnimationFrame(id);
    }
  }, [isInstant]);

  /* --- Infinite scroll controls --- */
  const handleNext = () => {
    setCurrentPos((p) => p + 1);
  };

  const handlePrev = () => {
    setCurrentPos((p) => p - 1);
  };

  /* --- Handle teleport after crossing clones --- */
  const handleTransitionEnd = () => {
    // If we moved right into head clones, teleport back to first real
    if (currentPos === lastRealIndex + 1) {
      setIsInstant(true);
      setCurrentPos(firstRealIndex);
    }
    // If we moved left into tail clones, teleport to last real
    else if (currentPos === firstRealIndex - 1) {
      setIsInstant(true);
      setCurrentPos(lastRealIndex);
    }
  };

  /* --- Geometry --- */
  const cardSpacing = CARD_W + GAP;
  const offset = -(currentPos * cardSpacing);
  const sliderWidth = extendedLength * cardSpacing;

  // Map extended index -> actual testimonial index (modulo)
  const getRealIndex = (extendedIndex) => {
    const x = extendedIndex - visibleCards; // shift so real region starts at 0
    return ((x % total) + total) % total; // safe modulo
  };

  return (
    <div className="w-full max-w-7xl px-4 py-12 mx-auto">
      <div className="overflow-hidden">
        {/* ===================== SLIDER ===================== */}
        <div
          className="flex"
          style={{
            width: sliderWidth,
            transform: `translateX(${offset}px)`,
            transition: isInstant ? "none" : "transform 0.5s ease-in-out",
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {Array.from({ length: extendedLength }).map((_, i) => {
            const realIndex = getRealIndex(i);
            const t = testimonials[realIndex];

            return (
              <div
                key={`${i}-${realIndex}`}
                onClick={() =>
                  isMobile &&
                  setActiveCard(activeCard === realIndex ? null : realIndex)
                }
                className="relative group flex-shrink-0 cursor-pointer"
                style={{
                  width: CARD_W,
                  height: CARD_H,
                  marginRight: GAP,
                }}
              >
                <img
                  src={t.img}
                  alt={t.name}
                  className="w-full h-full object-cover rounded-lg"
                />

                {/* ===================== OVERLAY ===================== */}
                <div
                  className={`
                    absolute inset-0 bg-[#003DA5] text-white rounded-lg
                    transition-opacity duration-300
                    flex items-center justify-center p-8
                    ${
                      isMobile
                        ? activeCard === realIndex
                          ? "opacity-95"
                          : "opacity-0"
                        : "opacity-0 group-hover:opacity-95"
                    }
                  `}
                >
                  <div className="text-center">
                    <p className="italic mb-4 text-sm leading-relaxed">
                      "{t.quote}"
                    </p>
                    <div className="font-semibold text-lg">{t.name}</div>
                    <div className="text-sm opacity-90 mt-1">{t.role}</div>
                    <div className="text-xs opacity-70 mt-1">{t.subtitle}</div>
                  </div>
                </div>

                {isMobile && activeCard !== realIndex && (
                  <div className="absolute bottom-3 right-3 text-xs bg-black/50 text-white px-2 py-1 rounded">
                    Tap to read
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ===================== CONTROLS ===================== */}
        <div className="mt-8 flex justify-end gap-8">
          <Arrow direction="left" onClick={handlePrev} />
          <Arrow direction="right" onClick={handleNext} />
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSection;
